class SliderBar {
  constructor(options = {}) {
    // Configuration options
    this.options = {
      width: "",
      height: "",
      beforeImg: null,
      afterImg: null,
      line: true,
      lineColor: "",
    };

    // Local state
    this.localData = {
      sliderContainer: null,
      sliderWrap: null,
      beforeWrap: null,
      sliderBtn: null,
      marginX: 0,
      isDragging: false,
      startX: 0,
      activePointerId: null,
    };

    // Bound handlers
    this.handleStart = this.handleStart.bind(this);
    this.handleMove = this.handleMove.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleWrapClick = this.handleWrapClick.bind(this);

    // Merge options
    this.options = { ...this.options, ...options };
    this.el = document.querySelector(options.el);

    // Initialize
    this.init(this.el);
  }

  // Check if device supports pointer events
  supportsPointerEvents() {
    return window.PointerEvent !== undefined;
  }

  // Check if device supports touch
  supportsTouchEvents() {
    return "ontouchstart" in window || navigator.maxTouchPoints > 0;
  }

  // Add event listeners with fallback support
  addListener() {
    const sliderBtn = this.localData.sliderBtn;
    const sliderWrap = this.localData.sliderWrap;

    // Configure touch behavior
    sliderWrap.style.touchAction = "none";
    sliderWrap.style.userSelect = "none";
    sliderWrap.style.webkitUserSelect = "none";
    sliderWrap.style.msUserSelect = "none";

    if (this.supportsPointerEvents()) {
      // Modern approach: Use Pointer Events (works for mouse, touch, pen)
      sliderBtn.addEventListener("pointerdown", this.handleStart);
      sliderWrap.addEventListener("pointermove", this.handleMove);
      sliderWrap.addEventListener("pointerup", this.handleEnd);
      sliderWrap.addEventListener("pointercancel", this.handleEnd);
      sliderWrap.addEventListener("pointerleave", this.handleEnd);

      // Enable pointer capture for better tracking
      sliderBtn.addEventListener("pointerdown", (e) => {
        sliderBtn.setPointerCapture(e.pointerId);
      });
    } else {
      // Fallback: Use separate touch and mouse events
      if (this.supportsTouchEvents()) {
        sliderBtn.addEventListener("touchstart", this.handleStart, {
          passive: false,
        });
        sliderWrap.addEventListener("touchmove", this.handleMove, {
          passive: false,
        });
        sliderWrap.addEventListener("touchend", this.handleEnd);
        sliderWrap.addEventListener("touchcancel", this.handleEnd);
      }

      // Always add mouse events for desktop/hybrid support
      sliderBtn.addEventListener("mousedown", this.handleStart);
      sliderWrap.addEventListener("mousemove", this.handleMove);
      sliderWrap.addEventListener("mouseup", this.handleEnd);
      sliderWrap.addEventListener("mouseleave", this.handleEnd);
    }

    // Add click-to-position functionality
    sliderWrap.addEventListener("click", this.handleWrapClick);

    // Prevent image dragging and context menu
    const images = sliderWrap.querySelectorAll("img");
    images.forEach((img) => {
      img.addEventListener("dragstart", (e) => e.preventDefault());
      img.addEventListener("contextmenu", (e) => e.preventDefault());
      img.style.pointerEvents = "none"; // Prevent image interaction
    });

    // Prevent text selection
    sliderWrap.addEventListener("selectstart", (e) => e.preventDefault());
  }

  // Get coordinates from event (unified for all event types)
  getCoords(e) {
    // Pointer events
    if (e.pointerId !== undefined) {
      return { x: e.clientX, y: e.clientY };
    }
    // Touch events
    if (e.touches && e.touches.length > 0) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    // Mouse events
    return { x: e.clientX, y: e.clientY };
  }

  // Handle click on wrapper to jump to position
  handleWrapClick(e) {
    if (this.localData.isDragging) return;

    const coords = this.getCoords(e);
    const rect = this.localData.sliderWrap.getBoundingClientRect();
    let newX = coords.x - rect.left;

    // Clamp to boundaries
    newX = Math.max(0, Math.min(newX, rect.width));

    // Update positions with smooth transition
    this.localData.sliderBtn.style.transition = "left 0.2s ease";
    this.localData.beforeWrap.style.transition = "width 0.2s ease";

    this.localData.sliderBtn.style.left = newX + "px";
    this.localData.beforeWrap.style.width = newX + "px";

    // Update ARIA value
    const percentage = Math.round((newX / rect.width) * 100);
    this.localData.sliderBtn.setAttribute("aria-valuenow", percentage);

    // Remove transition after animation
    setTimeout(() => {
      this.localData.sliderBtn.style.transition = "";
      this.localData.beforeWrap.style.transition = "";
    }, 200);
  }

  // Handle start of drag
  handleStart(e) {
    // Prevent multiple pointer interactions
    if (this.localData.isDragging) return;

    e.preventDefault();
    e.stopPropagation();

    // Store active pointer ID for pointer events
    if (e.pointerId !== undefined) {
      this.localData.activePointerId = e.pointerId;
    }

    this.localData.isDragging = true;
    const coords = this.getCoords(e);
    const rect = this.localData.sliderWrap.getBoundingClientRect();

    // Calculate initial offset from button center
    const btnRect = this.localData.sliderBtn.getBoundingClientRect();
    this.localData.marginX = coords.x - btnRect.left - btnRect.width / 2;

    // Add active class
    this.localData.sliderBtn.classList.add("active");

    // Update cursor
    document.body.style.cursor = "grabbing";
    this.localData.sliderWrap.style.cursor = "grabbing";

    // Remove any existing transitions
    this.localData.sliderBtn.style.transition = "";
    this.localData.beforeWrap.style.transition = "";
  }

  // Handle move
  handleMove(e) {
    if (!this.localData.isDragging) return;

    // Check if this is the correct pointer (for pointer events)
    if (
      e.pointerId !== undefined &&
      e.pointerId !== this.localData.activePointerId
    ) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const coords = this.getCoords(e);
    const rect = this.localData.sliderWrap.getBoundingClientRect();

    // Calculate new position
    let newX = coords.x - rect.left - this.localData.marginX;

    // Clamp to boundaries
    newX = Math.max(0, Math.min(newX, rect.width));

    // Update positions
    this.localData.sliderBtn.style.left = newX + "px";
    this.localData.beforeWrap.style.width = newX + "px";

    // Update ARIA value
    const percentage = Math.round((newX / rect.width) * 100);
    this.localData.sliderBtn.setAttribute("aria-valuenow", percentage);
  }

  // Handle end of drag
  handleEnd(e) {
    if (!this.localData.isDragging) return;

    // Check if this is the correct pointer (for pointer events)
    if (
      e.pointerId !== undefined &&
      e.pointerId !== this.localData.activePointerId
    ) {
      return;
    }

    e.preventDefault();
    this.localData.isDragging = false;
    this.localData.activePointerId = null;

    // Remove active class
    this.localData.sliderBtn.classList.remove("active");

    // Reset cursor
    document.body.style.cursor = "";
    this.localData.sliderWrap.style.cursor = "";
  }

  // Initialize slider
  init(el) {
    const { beforeImg, afterImg, width, height, line, lineColor } =
      this.options;

    if (!beforeImg || !afterImg) {
      console.error("SliderBar: beforeImg and afterImg are required");
      return;
    }

    // Create HTML structure
    el.innerHTML = `
      <div class="slider-container">
        <div class="slider-wrap">
          <div class="before-img">
            <img src="${beforeImg}" alt="Before image" draggable="false">
          </div>
          <img src="${afterImg}" alt="After image" class="after-img" draggable="false">
          <span class="slider-btn" 
                role="slider" 
                aria-label="Slide to compare images" 
                aria-valuemin="0" 
                aria-valuemax="100" 
                aria-valuenow="50"
                tabindex="0"></span>
        </div>
      </div>
    `;

    // Cache DOM references
    this.localData.sliderContainer = el.querySelector(".slider-container");
    this.localData.sliderWrap = el.querySelector(".slider-wrap");
    this.localData.beforeWrap = el.querySelector(".before-img");
    this.localData.sliderBtn = el.querySelector(".slider-btn");

    // Apply base styles
    this.applyStyles();

    // Set initial position to center
    this.setInitialPosition();

    // Add event listeners
    this.addListener();

    // Add keyboard support
    this.addKeyboardSupport();

    // Handle resize
    this.addResizeHandler();
  }

  // Apply CSS styles
  applyStyles() {
    const { width, height, line, lineColor } = this.options;

    // Container styles
    this.el.style.cssText = "display: flex; justify-content: center;";
    this.localData.sliderContainer.style.cssText = `
      width: ${width}; 
      height: ${height}; 
      position: relative; 
      border-radius: 4px; 
      overflow: hidden;
    `;

    // Wrap styles
    this.localData.sliderWrap.style.cssText = `
      position: relative; 
      width: 100%; 
      height: 100%; 
      overflow: hidden;
      cursor: crosshair;
    `;

    // Before wrapper styles
    this.localData.beforeWrap.style.cssText = `
      position: absolute; 
      top: 0; 
      left: 0; 
      height: 100%; 
      width: 50%;
      overflow: hidden; 
      z-index: 2;
    `;

    // After image styles
    const afterImg = this.localData.sliderWrap.querySelector(".after-img");
    afterImg.style.cssText = `
      width: 100%; 
      height: 100%; 
      object-fit: cover; 
      display: block;
    `;

    // Before image styles
    const beforeImg = this.localData.beforeWrap.querySelector("img");
    beforeImg.style.cssText = `
      width: ${this.localData.sliderContainer.offsetWidth}px; 
      height: 100%; 
      object-fit: cover; 
      display: block;
    `;

    // Slider button styles
    const btnStyle = `
      position: absolute; 
      top: 0; 
      left: 50%;
      width: 4px;
      height: 100%; 
      cursor: grab;
      z-index: 3;
      outline: none;
      transform: translateX(-50%);
    `;

    if (!line) {
      this.localData.sliderBtn.style.cssText = btnStyle + "background: none;";
    } else {
      const bgColor = lineColor || "rgba(255, 255, 255, 0.8)";
      this.localData.sliderBtn.style.cssText =
        btnStyle +
        `
        background: ${bgColor}; 
        box-shadow: 0 0 0 1px rgba(0,0,0,0.3);
      `;
    }

    // Add active state styles
    const style = document.createElement("style");
    style.textContent = `
      .slider-btn.active {
        cursor: grabbing !important;
      }
      .slider-btn:focus {
        box-shadow: 0 0 0 2px #007bff !important;
      }
    `;
    document.head.appendChild(style);
  }

  // Set initial position
  setInitialPosition() {
    setTimeout(() => {
      const rect = this.localData.sliderWrap.getBoundingClientRect();
      const initialPosition = rect.width / 2;
      this.localData.sliderBtn.style.left = initialPosition + "px";
      this.localData.beforeWrap.style.width = initialPosition + "px";

      // Update before image width to match container
      const beforeImg = this.localData.beforeWrap.querySelector("img");
      beforeImg.style.width = rect.width + "px";
    }, 0);
  }

  // Add keyboard support
  addKeyboardSupport() {
    this.localData.sliderBtn.addEventListener("keydown", (e) => {
      const rect = this.localData.sliderWrap.getBoundingClientRect();
      const currentPos =
        parseFloat(this.localData.sliderBtn.style.left) || rect.width / 2;
      const step = rect.width * 0.05; // 5% steps
      let newPos = currentPos;

      switch (e.key) {
        case "ArrowLeft":
          newPos = Math.max(0, currentPos - step);
          break;
        case "ArrowRight":
          newPos = Math.min(rect.width, currentPos + step);
          break;
        case "Home":
          newPos = 0;
          break;
        case "End":
          newPos = rect.width;
          break;
        default:
          return; // Don't prevent default for other keys
      }

      e.preventDefault();
      this.localData.sliderBtn.style.left = newPos + "px";
      this.localData.beforeWrap.style.width = newPos + "px";

      const percentage = Math.round((newPos / rect.width) * 100);
      this.localData.sliderBtn.setAttribute("aria-valuenow", percentage);
    });
  }

  // Add resize handler
  addResizeHandler() {
    let resizeTimeout;
    const resizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const rect = this.localData.sliderWrap.getBoundingClientRect();
        const centerPosition = rect.width / 2;
        this.localData.sliderBtn.style.left = centerPosition + "px";
        this.localData.beforeWrap.style.width = centerPosition + "px";
        this.localData.sliderBtn.setAttribute("aria-valuenow", "50");

        // Update before image width
        const beforeImg = this.localData.beforeWrap.querySelector("img");
        beforeImg.style.width = rect.width + "px";
      }, 100);
    };

    window.addEventListener("resize", resizeHandler);

    // Store for cleanup
    this.localData.resizeHandler = resizeHandler;
  }

  // Public method to set position programmatically
  setPosition(percentage) {
    const clampedPercentage = Math.max(0, Math.min(100, percentage));
    const rect = this.localData.sliderWrap.getBoundingClientRect();
    const position = (rect.width * clampedPercentage) / 100;

    this.localData.sliderBtn.style.left = position + "px";
    this.localData.beforeWrap.style.width = position + "px";
    this.localData.sliderBtn.setAttribute("aria-valuenow", clampedPercentage);
  }

  // Clean up method
  destroy() {
    const sliderBtn = this.localData.sliderBtn;
    const sliderWrap = this.localData.sliderWrap;

    if (!sliderBtn || !sliderWrap) return;

    // Remove pointer events
    if (this.supportsPointerEvents()) {
      sliderBtn.removeEventListener("pointerdown", this.handleStart);
      sliderWrap.removeEventListener("pointermove", this.handleMove);
      sliderWrap.removeEventListener("pointerup", this.handleEnd);
      sliderWrap.removeEventListener("pointercancel", this.handleEnd);
      sliderWrap.removeEventListener("pointerleave", this.handleEnd);
    } else {
      // Remove touch events
      sliderBtn.removeEventListener("touchstart", this.handleStart);
      sliderWrap.removeEventListener("touchmove", this.handleMove);
      sliderWrap.removeEventListener("touchend", this.handleEnd);
      sliderWrap.removeEventListener("touchcancel", this.handleEnd);

      // Remove mouse events
      sliderBtn.removeEventListener("mousedown", this.handleStart);
      sliderWrap.removeEventListener("mousemove", this.handleMove);
      sliderWrap.removeEventListener("mouseup", this.handleEnd);
      sliderWrap.removeEventListener("mouseleave", this.handleEnd);
    }

    // Remove other events
    sliderWrap.removeEventListener("click", this.handleWrapClick);

    // Remove resize handler
    if (this.localData.resizeHandler) {
      window.removeEventListener("resize", this.localData.resizeHandler);
    }

    // Reset cursor
    document.body.style.cursor = "";

    // Clear HTML
    this.el.innerHTML = "";

    // Clear references
    Object.keys(this.localData).forEach((key) => {
      this.localData[key] = null;
    });
  }
}
